import { useEffect } from "react";
import { useCommandRegistry, useRunCommand } from "@/commands/CommandRegistryContext";
import type { CurrencyFormat } from "@rubyplay/effw-connector";
import { CHEAT_COMMANDS } from "@/cheats/cheatCommandNames";
import type { WidgetPositionPatch } from "@/cheats/EFProtocolCheatCommands";
import type { TooltipPosition } from "@/integrations/captainup/CaptainUpTypes";
import type { ActiveDockTarget } from "@/orchestrators/useCaptainUpIntegration";

export function Cheats() {
	const runCommand = useRunCommand();
	const registry = useCommandRegistry();

	useEffect(() => {
		if (typeof window === "undefined") return;

		type CheatsApi = {
			run?: (name: string, payload?: unknown) => void;
			has?: (name: string) => boolean;

			setCaptainUpActiveDockWidget?: (widget: ActiveDockTarget) => void;
			setCaptainUpTooltipPosition?: (position: TooltipPosition) => void;
			openCaptainUpPopup?: () => void;
			closeCaptainUpPopup?: () => void;
			retryCaptainUpBoot?: () => void;

			sendHeartBeat?: () => void;
			sendFatalError?: (payload?: {
				category?: "RUNTIME" | "UNHANDLED_REJECTION";
				message?: string;
				stack?: string;
			}) => void;
			sendClientReady?: () => void;
			sendBetUpdated?: (payload: {
				bet: number;
				currencyFormat?: CurrencyFormat;
			}) => void;

			triggerCaptainUpAuthTimeout?: () => void;
			triggerCaptainUpAuthBadRequest?: () => void;
			triggerCaptainUpAuthNotFound?: () => void;
			triggerCaptainUpAuthServerError?: () => void;
			triggerCaptainUpSdkLoadFailure?: () => void;
			triggerCaptainUpSdkInitFailure?: () => void;
			triggerAllHitAreaWidgetSnapshots?: () => void;
			setWidgetPosition?: (patch: WidgetPositionPatch) => void;
		};

		const w = window as typeof window & { EFW_CHEATS?: CheatsApi };
		const cheats: CheatsApi = {};

		cheats.run = (name, payload) => {
			void runCommand(name, payload);
		};

		cheats.has = (name) => registry.has(name);

		cheats.setCaptainUpActiveDockWidget = (widget) => {
			void runCommand(CHEAT_COMMANDS.captainUp.setActiveDockWidget, widget);
		};

		cheats.setCaptainUpTooltipPosition = (position) => {
			void runCommand(CHEAT_COMMANDS.captainUp.setTooltipPosition, position);
		};

		cheats.openCaptainUpPopup = () => {
			void runCommand(CHEAT_COMMANDS.captainUp.openPopup);
		};

		cheats.closeCaptainUpPopup = () => {
			void runCommand(CHEAT_COMMANDS.captainUp.closePopup);
		};

		cheats.retryCaptainUpBoot = () => {
			void runCommand(CHEAT_COMMANDS.captainUp.retryBoot);
		};

		cheats.setWidgetPosition = (patch) => {
			void runCommand(CHEAT_COMMANDS.ef.setWidgetPosition, patch);
		};

		cheats.sendHeartBeat = () => {
			void runCommand(CHEAT_COMMANDS.ef.sendHeartBeat);
		};

		cheats.sendFatalError = (payload) => {
			void runCommand(CHEAT_COMMANDS.ef.sendFatalError, payload);
		};

		cheats.sendClientReady = () => {
			void runCommand(CHEAT_COMMANDS.ef.sendClientReady);
		};

		cheats.sendBetUpdated = (payload) => {
			void runCommand(CHEAT_COMMANDS.ef.sendBetUpdated, payload);
		};

		cheats.triggerCaptainUpAuthTimeout = () => {
			void runCommand(CHEAT_COMMANDS.ef.triggerCaptainUpAuthTimeout);
		};

		cheats.triggerCaptainUpAuthBadRequest = () => {
			void runCommand(CHEAT_COMMANDS.ef.triggerCaptainUpAuthBadRequest);
		};

		cheats.triggerCaptainUpAuthNotFound = () => {
			void runCommand(CHEAT_COMMANDS.ef.triggerCaptainUpAuthNotFound);
		};

		cheats.triggerCaptainUpAuthServerError = () => {
			void runCommand(CHEAT_COMMANDS.ef.triggerCaptainUpAuthServerError);
		};

		cheats.triggerCaptainUpSdkLoadFailure = () => {
			void runCommand(CHEAT_COMMANDS.ef.triggerCaptainUpSdkLoadFailure);
		};

		cheats.triggerCaptainUpSdkInitFailure = () => {
			void runCommand(CHEAT_COMMANDS.ef.triggerCaptainUpSdkInitFailure);
		};

		cheats.triggerAllHitAreaWidgetSnapshots = () => {
			void runCommand(CHEAT_COMMANDS.ef.triggerAllHitAreaWidgetSnapshots);
		};

		w.EFW_CHEATS = cheats;

		return () => {
			if (w.EFW_CHEATS === cheats) {
				delete w.EFW_CHEATS;
			}
		};
	}, [registry, runCommand]);

	return null;
}
